import { Observable, of } from 'rxjs'
import { StateObservable, combineEpics } from 'redux-observable'
import { filter, withLatestFrom, mergeMap, map, buffer, debounceTime } from 'rxjs/operators'
import { GlobalState } from 'store'
import { NegotiationStatus, EntityType, Negotiation, Application, NegotiationUpdate, NegotiationSide } from 'api'
import { penultimate } from 'utils'
import { ofEntityUpdateType } from './epicUtils'
import { ActivityMonitorActions } from 'store/activityMonitor/activityMonitorActions'
import { GridId } from 'store/grid'
import { SelectionActions } from 'store/selection'
import { isOwnTradingEntityGetterSelector } from 'selectors/adminSelectors'
import { hideExpiredRejectedSelector } from 'selectors/gridSelectors'
import { negotiationRepositoryEntryGetterSelector } from 'selectors/negotiations'
import { SYSTEM_USER_NAME } from 'model'
import { Activity } from '../store/activityMonitor'
import { NotificationsToastActions } from './toastEpic'
import { isSummaryNegotiationType } from '../view/routes/requests/shared/utils'
import { BTQAction } from 'epics'

const notificationStatuses = [
  NegotiationStatus.REQUESTED_WITHOUT_PRICE,
  NegotiationStatus.REQUESTED_WITH_PRICE,
  NegotiationStatus.CANCELLED,
  NegotiationStatus.QUOTED,
  NegotiationStatus.ACCEPTED,
  NegotiationStatus.REJECTED,
  NegotiationStatus.WITHDRAWN
]

const activityCompleteStatuses = [
  NegotiationStatus.REQUESTED_WITH_PRICE,
  NegotiationStatus.WITHDRAWN,
  NegotiationStatus.CANCELLED,
  NegotiationStatus.ACCEPTED,
  NegotiationStatus.REJECTED,
  NegotiationStatus.MISSED,
  NegotiationStatus.EXPIRED
]

function isNegotiationFromSameSide(negotiationUpdate: NegotiationUpdate) {
  return (
    (negotiationUpdate.negotiationSide === NegotiationSide.INITIATOR &&
      negotiationUpdate.entity.lastUpdatedBy === negotiationUpdate.entity.lastInitiatorId) ||
    (negotiationUpdate.negotiationSide === NegotiationSide.RESPONDER &&
      negotiationUpdate.entity.lastUpdatedBy === negotiationUpdate.entity.lastResponderId) ||
    negotiationUpdate.entity.lastUpdatedBy === SYSTEM_USER_NAME
  )
}

function isNegotiationFromOtherSide(negotiationUpdate: NegotiationUpdate) {
  return (
    (negotiationUpdate.negotiationSide === NegotiationSide.INITIATOR &&
      negotiationUpdate.entity.lastUpdatedBy === negotiationUpdate.entity.lastResponderId) ||
    (negotiationUpdate.negotiationSide === NegotiationSide.RESPONDER &&
      negotiationUpdate.entity.lastUpdatedBy === negotiationUpdate.entity.lastInitiatorId)
  )
}

function isNegotiationSummary(negotiationUpdate: NegotiationUpdate) {
  return isSummaryNegotiationType(negotiationUpdate.entity.negotiationType)
}

function negotiationStatusChangedFilter([negotiationUpdate, state]: [NegotiationUpdate, GlobalState]) {
  const repositoryEntries = negotiationRepositoryEntryGetterSelector(state)(negotiationUpdate.negotiationSide)
  const oldNegotiationRepositoryEntry = repositoryEntries[negotiationUpdate.entityId]
  const oldNegotiation = oldNegotiationRepositoryEntry?.lastUpdate.entity
  return !oldNegotiation || negotiationUpdate.entity.status !== oldNegotiation.status
}

function isAtStatusForNotification(negotiationUpdate: NegotiationUpdate): boolean {
  return notificationStatuses.includes(negotiationUpdate.entity.status)
}

function getGridForActivityIndicator(negotiation: Negotiation, state: GlobalState): GridId | null {
  const isAdmin = state.connectionStore.authorisedUser && state.connectionStore.authorisedUser.application === Application.ADMIN_GATEWAY

  const ownOrder = isOwnTradingEntityGetterSelector(state)(negotiation.fundId)

  const hideExpiredRejected = hideExpiredRejectedSelector(state)

  switch (negotiation.status) {
    case NegotiationStatus.REQUESTED_WITHOUT_PRICE:
    case NegotiationStatus.REQUESTED_WITH_PRICE:
      return GridId.IncomingGrid

    case NegotiationStatus.QUOTED:
      return GridId.InitiatedGrid

    case NegotiationStatus.REJECTED:
      if (isAdmin) {
        return negotiation.lastUpdatedBy === negotiation.lastResponderId ? (hideExpiredRejected ? null : GridId.InitiatedGrid) : null
      } else {
        return ownOrder ? (hideExpiredRejected ? null : GridId.InitiatedGrid) : null
      }

    case NegotiationStatus.WITHDRAWN:
      if (isAdmin) {
        return negotiation.lastUpdatedBy === negotiation.lastResponderId ? GridId.InitiatedGrid : GridId.IncomingGrid
      } else {
        return ownOrder ? GridId.InitiatedGrid : GridId.IncomingGrid
      }

    default:
      return null
  }
}

export function rfqEventReceivedEpic(
  action$: Observable<BTQAction>,
  state$: StateObservable<GlobalState>,
  _dependencies: {}
): Observable<BTQAction> {
  const activityAction$ = action$.pipe(
    ofEntityUpdateType(EntityType.NEGOTIATION),
    filter(isAtStatusForNotification),
    withLatestFrom(state$.pipe(penultimate())),
    filter(negotiationStatusChangedFilter),
    map(([negotiationUpdate, _]) => negotiationUpdate),
    filter(negotiationUpdate => isNegotiationFromOtherSide(negotiationUpdate)),
    filter(negotiationUpdate => !isNegotiationSummary(negotiationUpdate))
  )

  return activityAction$.pipe(
    buffer(activityAction$.pipe(debounceTime(250))),
    mergeMap(negotiationUpdates => {
      const actions: BTQAction[] = []

      actions.push(
        ActivityMonitorActions.createActivities(
          negotiationUpdates.map(negotiationUpdate => ({
            id: negotiationUpdate.entityId,
            negotiationUpdate
          }))
        )
      )

      const gridsWithActivity = new Set<GridId>()

      negotiationUpdates.forEach(negotiationUpdate => {
        const gridForActivityIndicator = getGridForActivityIndicator(negotiationUpdate.entity, state$.value)
        if (gridForActivityIndicator) {
          gridsWithActivity.add(gridForActivityIndicator)
        }

        actions.push(
          NotificationsToastActions.createRfqToast(
            negotiationUpdate.entityId,
            negotiationUpdate.negotiationSide === NegotiationSide.INITIATOR
          )
        )
      })

      gridsWithActivity.forEach(gridId => {
        actions.push(SelectionActions.setGridActivityHighlight(gridId, true))
      })

      return of(...actions)
    })
  )
}

function isAtStatusForActivityComplete(negotiationUpdate: NegotiationUpdate): boolean {
  return activityCompleteStatuses.includes(negotiationUpdate.entity.status)
}

export function rfqEventSentEpic(
  action$: Observable<BTQAction>,
  state$: StateObservable<GlobalState>,
  _dependencies: {}
): Observable<BTQAction> {
  const rfqSentAction$ = action$.pipe(
    ofEntityUpdateType(EntityType.NEGOTIATION),
    filter(isAtStatusForActivityComplete),
    withLatestFrom(state$.pipe(penultimate())),
    filter(negotiationStatusChangedFilter),
    map(([negotiationUpdate, _]) => negotiationUpdate),
    filter(negotiationUpdate => isNegotiationFromSameSide(negotiationUpdate)),
    filter(negotiationUpdate => !isNegotiationSummary(negotiationUpdate))
  )

  return rfqSentAction$.pipe(
    buffer(rfqSentAction$.pipe(debounceTime(250))),
    map(negotiationUpdates => {
      const activities = negotiationUpdates.reduce<Record<string, Activity>>((activityRecord, negotiationUpdate) => {
        activityRecord[negotiationUpdate.entityId] = {
          id: negotiationUpdate.entityId,
          negotiationUpdate
        }
        return activityRecord
      }, {})

      return ActivityMonitorActions.completeActivities(activities)
    })
  )
}

export const rfqEventEpic = combineEpics(rfqEventReceivedEpic, rfqEventSentEpic)
